import { createContext, useCallback, useEffect, useState } from "react";
import { getAccessToken, getLoggedinUserId, heartbeatAPI } from "services/auth";
import { useApolloClient, useQuery } from "@apollo/client";
import { QUERY_PRODUCT_ENTITLEMENTS } from "services/graphQL/queries";
import { getHasuraClaims } from "utils/jwt-utils";
import * as Sentry from "@sentry/react";
import useChangeEventPolling, {
  TAuditChangeLog
} from "change-events/change-event-polling";

export enum RoleSubscriptionEnum {
  none = 0,
  subscription_viewer,
  subscription_editor,
  subscription_admin
}

type ContextType = {
  subscriptionRole: RoleSubscriptionEnum;
  subscriptionId: string | undefined;
  eventLogs: Array<TAuditChangeLog>;
  isFeatureFlagEnabled: (feature: string) => boolean;
  isProductEntitlementsDataAvailable: boolean;
};

export const SubscriptionContext = createContext<ContextType>({
  subscriptionRole: RoleSubscriptionEnum.none,
  subscriptionId: undefined,
  eventLogs: [],
  isFeatureFlagEnabled: () => false,
  isProductEntitlementsDataAvailable: false
});
export interface SubscriptionProviderProps {
  children: React.ReactNode;
}

function SubscriptionProvider({
  children
}: Readonly<SubscriptionProviderProps>) {
  const token = getAccessToken();
  const apolloClient = useApolloClient();

  const { data: productEntitlementsData } = useQuery<{
    get_product_entitlements: Array<{
      feature: string;
      enabled: boolean;
    }>;
  }>(QUERY_PRODUCT_ENTITLEMENTS);

  const isFeatureFlagEnabled = useCallback(
    (feature: string) => {
      if (productEntitlementsData?.get_product_entitlements) {
        return productEntitlementsData?.get_product_entitlements.some(
          (f) => f.enabled && f.feature === feature
        );
      }
      return false;
    },
    [productEntitlementsData?.get_product_entitlements]
  );

  const [contextState, setContextState] = useState<ContextType>({
    subscriptionRole: RoleSubscriptionEnum.none,
    subscriptionId: undefined,
    eventLogs: [],
    isFeatureFlagEnabled,
    isProductEntitlementsDataAvailable: false
  });

  const { data: eventLogs } = useChangeEventPolling({
    gqlClient: apolloClient,
    subscriptionId: contextState.subscriptionId
  });

  useEffect(() => {
    if (productEntitlementsData) {
      setContextState((pre) => ({
        ...pre,
        isFeatureFlagEnabled,
        isProductEntitlementsDataAvailable: true
      }));
    }
  }, [isFeatureFlagEnabled, productEntitlementsData]);

  useEffect(() => {
    setContextState((pre) =>
      // True arm unreachable: effect deps are [eventLogs], so the effect only
      // runs when eventLogs changed reference, meaning pre.eventLogs (the prior
      // ref) can never equal the new eventLogs on a deterministic effect run.
      /* v8 ignore next */
      pre.eventLogs === eventLogs ? pre : { ...pre, eventLogs }
    );
  }, [eventLogs]);

  useEffect(() => {
    if (!token) return;

    const hasuraClaims = getHasuraClaims(token);

    if (!hasuraClaims) return;

    const role = hasuraClaims?.["x-hasura-default-role"];
    const subscriptionId = hasuraClaims?.["x-hasura-subscription-id"];
    let subscriptionRole = RoleSubscriptionEnum.none;

    switch (role) {
      case "subscription_admin":
        subscriptionRole = RoleSubscriptionEnum.subscription_admin;
        break;
      case "subscription_editor":
        subscriptionRole = RoleSubscriptionEnum.subscription_editor;
        break;
      case "subscription_viewer":
        subscriptionRole = RoleSubscriptionEnum.subscription_viewer;
        break;
      default:
        subscriptionRole = RoleSubscriptionEnum.none;
        break;
    }
    setContextState((pre) => ({ ...pre, subscriptionRole, subscriptionId }));

    const HEARTBEAT_INTERVAL_TIME_MILISECOND = 1000 * 60 * 5; // 5 min
    setInterval(heartbeatAPI, HEARTBEAT_INTERVAL_TIME_MILISECOND);
    Sentry.setUser({ id: getLoggedinUserId() || "unknown" });
  }, [token]);

  return (
    <SubscriptionContext.Provider value={contextState}>
      {children}
    </SubscriptionContext.Provider>
  );
}

export default SubscriptionProvider;
